import React, { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import { CustomPagination, Loading, CustomBreadCrumb } from "../../../components";
import { PaginationType, TicketDepartmentType, TicketType } from "../../../DTO";
import { useAppNavigate, useAppPagination, useAppQuery } from "../../../Router/hook";
import { TicketApi } from "../../../api";
import { useAppSelector } from "../../../redux-config/hooks";
import { selectUser } from "../../../redux-config/entities/user";

const STATUS_BADGE: Record<number, { cls: string; label: string }> = {
    1: { cls: "app-user-badge app-user-badge-danger",   label: "Open" },
    2: { cls: "app-user-badge app-user-badge-warning",  label: "In Progress" },
    3: { cls: "app-user-badge app-user-badge-muted",    label: "Closed" },
    4: { cls: "app-user-badge app-user-badge-info",     label: "Waiting — User" },
    5: { cls: "app-user-badge app-user-badge-primary",  label: "Waiting — Support" },
};

const STATUS_OPTIONS = [
    { id: 1, label: "Open" },
    { id: 2, label: "In Progress" },
    { id: 3, label: "Closed" },
    { id: 4, label: "Waiting for User" },
    { id: 5, label: "Waiting for Support" },
];

const truncate = (text: string | null | undefined, max = 70) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "…" : text;
};

export const TicketsList: FC = () => {
    const [pagination, setPagination] =
        useState<PaginationType>(useAppPagination());
    const [tickets, setTickets] = useState<TicketType[]>([]);
    const [departments, setDepartments] = useState<TicketDepartmentType[]>([]);
    const [departmentId, setDepartmentId] = useState<number>(0);
    const [statusId, setStatusId] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useAppNavigate();
    const query = useAppQuery();
    const userId = query.get("user_id") ? Number(query.get("user_id")) : undefined;
    const user = useAppSelector(selectUser);

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "Tickets", href: "#" },
    ];

    const fetchTickets = (
        page: number,
        filters?: { department_id?: number; status_id?: number; user_id?: number },
    ) => {
        setLoading(true);
        TicketApi.ListTickets(user, page, filters)
            .then((data) => {
                setTickets(data.tickets);
                setPagination(data.pagination);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                Swal.fire({ icon: "error", text: err });
            });
    };

    useEffect(() => {
        TicketApi.ListTicketDepartments(user)
            .then((data) => setDepartments(data.departments))
            .catch(() => undefined);

        fetchTickets(pagination.current_page, { user_id: userId });
    }, []);

    const handleFilterChange = (newDepartmentId: number, newStatusId: number) => {
        setDepartmentId(newDepartmentId);
        setStatusId(newStatusId);
        fetchTickets(1, {
            department_id: newDepartmentId || undefined,
            status_id: newStatusId || undefined,
            user_id: userId,
        });
    };

    return (
        <>
            <Helmet>
                <title>D2Type | Tickets</title>
            </Helmet>
            <div className="d-flex flex-column">
                <CustomBreadCrumb breadCrumbList={crumbs} />
                {loading ? (
                    <Loading />
                ) : (
                    <div className="d-flex flex-column bg-custom-white rounded p-3 gap-3">
                        {/* Header + filters */}
                        <div className="d-flex flex-row flex-wrap justify-content-between align-items-end gap-2 pb-2 border-bottom">
                            <div>
                                <h4 className="mb-0">Tickets</h4>
                                {userId && (
                                    <small className="text-muted">
                                        Filtered by user #{userId} —{" "}
                                        <Link to="/tickets" className="text-danger">
                                            clear
                                        </Link>
                                    </small>
                                )}
                            </div>
                            <div className="d-flex flex-row gap-3">
                                <div>
                                    <small className="text-muted d-block mb-1">Department</small>
                                    <select
                                        className="form-select form-select-sm"
                                        value={departmentId}
                                        onChange={(e) =>
                                            handleFilterChange(Number(e.target.value), statusId)
                                        }
                                    >
                                        <option value={0}>All Departments</option>
                                        {departments.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <small className="text-muted d-block mb-1">Status</small>
                                    <select
                                        className="form-select form-select-sm"
                                        value={statusId}
                                        onChange={(e) =>
                                            handleFilterChange(departmentId, Number(e.target.value))
                                        }
                                    >
                                        <option value={0}>All Statuses</option>
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <Table hover responsive className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: "3rem" }}>#</th>
                                    <th>Title / Last Message</th>
                                    <th>User</th>
                                    <th>Department</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th style={{ width: "5rem" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets.length > 0 ? (
                                    tickets.map((item, index) => {
                                        const badge =
                                            STATUS_BADGE[item.status_id] ??
                                            STATUS_BADGE[1];
                                        const rowNum =
                                            index +
                                            1 +
                                            (pagination.current_page - 1) *
                                                pagination.per_page;
                                        const userName = item.user
                                            ? `${item.user.first_name ?? ""} ${item.user.last_name ?? ""}`.trim() ||
                                              item.user.email
                                            : "-";
                                        const lastMsg = truncate(item.last_message ?? item.body);

                                        return (
                                            <tr
                                                key={item.id}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => navigate(`/tickets/${item.id}`)}
                                            >
                                                <td className="text-muted">{rowNum}</td>
                                                <td>
                                                    <div className="fw-semibold">{item.title}</div>
                                                    {lastMsg && (
                                                        <div
                                                            className="text-muted"
                                                            style={{ fontSize: "0.8rem" }}
                                                        >
                                                            {lastMsg}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: "0.85rem" }}>
                                                        {userName}
                                                    </div>
                                                    {item.user?.register_state === "personal_rejected" && (
                                                        <span
                                                            className="app-user-badge app-user-badge-danger"
                                                            style={{ fontSize: "0.65rem" }}
                                                        >
                                                            Reg. Rejected
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ fontSize: "0.85rem" }}>
                                                    {item.department?.title ?? "-"}
                                                </td>
                                                <td>
                                                    <span className={badge.cls}>
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td
                                                    style={{ fontSize: "0.8rem" }}
                                                    className="text-muted text-nowrap"
                                                >
                                                    {item.created_at?.slice(0, 10)}
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <Link
                                                        to={`/tickets/${item.id}`}
                                                        className="btn btn-sm btn-outline-secondary"
                                                    >
                                                        View
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center text-muted py-5"
                                        >
                                            No tickets found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <CustomPagination
                            total={pagination.total}
                            perPage={pagination.per_page}
                            currentPage={pagination.current_page}
                            changePage={(pageNum: number) => {
                                navigate(`/tickets?current_page=${pageNum}`);
                                fetchTickets(pageNum, {
                                    department_id: departmentId || undefined,
                                    status_id: statusId || undefined,
                                    user_id: userId,
                                });
                            }}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
